import { Building2, Check, ChevronsUpDown, Layers } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useTranslation } from "@/hooks/useTranslation";
import { useActiveBrand } from "@/hooks/useActiveBrand";
import { useMyBrandProfiles } from "@/hooks/useMarketplace";

/**
 * Picks which client brand the brand-side screens are scoped to.
 *
 * Renders nothing at all below two brands: an agency needs this, and a single
 * brand would only get a control with one option and no meaning. That keeps the
 * shell identical to what a one-brand account saw before brands became plural.
 *
 * "All brands" is a real option, not a null state — an agency wants the roster
 * view as often as it wants one client.
 */
export default function BrandSwitcher({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const { brands } = useMyBrandProfiles(enabled);
  const { activeBrandId, setActiveBrand } = useActiveBrand();

  if (brands.length < 2) return null;

  const active = brands.find((b) => b.id === activeBrandId) ?? null;

  return (
    <div className="px-3 pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 bg-white text-left hover:bg-neutral-50 transition-colors cursor-pointer">
          {active?.logoUrl ? (
            <img
              src={active.logoUrl}
              alt=""
              className="w-5 h-5 rounded object-cover flex-shrink-0"
            />
          ) : (
            <span className="w-5 h-5 rounded bg-neutral-100 text-neutral-400 flex items-center justify-center flex-shrink-0">
              {active ? <Building2 className="w-3 h-3" /> : <Layers className="w-3 h-3" />}
            </span>
          )}
          <span className="flex-1 min-w-0 truncate text-sm font-medium text-neutral-900">
            {active?.companyName ?? t("marketplace.brandSwitcher.allBrands")}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuItem
            onClick={() => setActiveBrand(null)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Layers className="w-3.5 h-3.5 text-neutral-400" />
            <span className="flex-1">{t("marketplace.brandSwitcher.allBrands")}</span>
            {activeBrandId === null && <Check className="w-3.5 h-3.5 text-steel-600" />}
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {brands.map((brand) => (
            <DropdownMenuItem
              key={brand.id}
              onClick={() => setActiveBrand(brand.id)}
              className="flex items-center gap-2 cursor-pointer"
            >
              {brand.logoUrl ? (
                <img src={brand.logoUrl} alt="" className="w-3.5 h-3.5 rounded object-cover" />
              ) : (
                <Building2 className="w-3.5 h-3.5 text-neutral-400" />
              )}
              <span className="flex-1 truncate">{brand.companyName}</span>
              {activeBrandId === brand.id && <Check className="w-3.5 h-3.5 text-steel-600" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
